class ProgressiveDiscount {
	/**
	 * Builds cumulative price table from ranges_price.
	 * Each entry represents the total cost for that quantity of photos.
	 *
	 * Example output: [
	 *   { photoCount: 1, total: 40, unitPrice: 40 },
	 *   { photoCount: 2, total: 70, unitPrice: 30 },
	 *   { photoCount: 3, total: 100, unitPrice: 30 },
	 * ]
	 */
	static validateRangesPrice(rangesPrice) {
		return Array.isArray(rangesPrice) && rangesPrice.length > 0;
	}

	static getSortedRangesPrice(rangesPrice) {
		return [...rangesPrice].sort((a, b) => a.min_photos - b.min_photos);
	}

	static findTotalRange(sorted, maxQuantity) {

		const totals = [];
		let accumulatedPrice = 0;

		for (let i = 1; i <= maxQuantity; i++) {
			const range = sorted.find(
				(r) => i >= r.min_photos && i <= r.max_photos
			) || sorted[sorted.length - 1];


			accumulatedPrice += range.price;
			totals.push({ photoCount: i, total: accumulatedPrice, unitPrice: range.price });
		}

		return totals;
	}

	static calculateRangeTotals(rangesPrice) {
		if (!ProgressiveDiscount.validateRangesPrice(rangesPrice)) return [];

		const sorted = ProgressiveDiscount.getSortedRangesPrice(rangesPrice);
		const lastRange = sorted[sorted.length - 1];
		const maxQuantity = lastRange.max_photos || lastRange.min_photos;

		return ProgressiveDiscount.findTotalRange(sorted, maxQuantity);
	}
	
	/**
	 * Returns the total cost for a given photoCount based on ranges_price.
	 */
	static getTotalPriceForPhotoCount(rangesPrice, photoCount) {
		const totals = ProgressiveDiscount.calculateRangeTotals(rangesPrice);
		const entry = totals.find((t) => t.photoCount === photoCount);

		if (entry) return entry.total;

		if (photoCount > totals.length && totals.length > 0) {
			const last = totals[totals.length - 1];
			const sorted = ProgressiveDiscount.getSortedRangesPrice(rangesPrice);
			const lastRange = sorted[sorted.length - 1];
			return last.total + (photoCount - last.photoCount) * lastRange.price;
		}

		return 0;
	}
	/**
	 * Finds which range a given photo count falls into.
	 */
	static findCurrentRange(rangesPrice, photoCount) {
		if (!rangesPrice || rangesPrice.length === 0 || photoCount <= 0) return null;

		const sorted = ProgressiveDiscount.getSortedRangesPrice(rangesPrice);
		const range = sorted.find(
			(r) => photoCount >= r.min_photos && photoCount <= r.max_photos
		);

		return range || sorted[sorted.length - 1];
	}

	
	static getAverageSavingsPercent(targetAverage, currentAverage) {
		return Math.round((1 - targetAverage / currentAverage) * 100);
		
	}
	
	static getRangeSavingsPercent(currentRangePrice, targetRangePrice) {
		
		return currentRangePrice > 0
		? Math.round((1 - targetRangePrice / currentRangePrice) * 100)
		: 0;
	}

	static calculatePhotosToDropAverage(targetTotal, targetCount, currentAverage, currentRangePrice) {
		return Math.floor(
			(targetTotal - targetCount * currentRangePrice) / (currentAverage - currentRangePrice)
		) + 1;
		
	}
	
	static hasOnlyIncreasingPrices(sorted) {
		return sorted.every((r, i) => i === 0 || r.price >= sorted[i - 1].price)
		&& sorted.some((r, i) => i > 0 && r.price > sorted[i - 1].price);
	}
	/**
	 * Determines the most persuasive incentive message based on average price.
	 *
	 * Priority for percentage messages:
	 *   1. averageSavingsPercent >= 50 → "cada foto sai X% mais barata"
	 *   2. rangeSavingsPercent >= 50 && photosNeeded === 1 → "ela sairá X% mais barata"
	 *   3. Otherwise → "cada foto sai por R$X" (average)
	 *
	 * If no future boundary lowers the average → lastTier fallback.
	 *
	 * Returns: {
	 *   type: 'incentive' | 'lastTier',
	 *   photosNeeded: number,
	 *   totalPhotos: number,
	 *   averagePrice: number,
	 *   additionalCost: number,
	 *   averageSavingsPercent: number,
	 *   rangeSavingsPercent: number,
	 *   currentTotal: number,
	 *   nextTotal: number,
	 * }
	 */
	static getIncentiveData(rangesPrice, currentPhotoCount, maxAvailablePhotos = Infinity) {
		if (!ProgressiveDiscount.validateRangesPrice(rangesPrice)) return null;

		const validPhotoCount = Math.max(currentPhotoCount, 0);
		if (validPhotoCount === 0) return null;
		if (validPhotoCount >= maxAvailablePhotos) return null;

		const currentTotal = ProgressiveDiscount.getTotalPriceForPhotoCount(rangesPrice, validPhotoCount);
		const currentAverage = currentTotal / validPhotoCount;

		const sorted = ProgressiveDiscount.getSortedRangesPrice(rangesPrice);
		const currentRange = ProgressiveDiscount.findCurrentRange(rangesPrice, validPhotoCount);
		if (!currentRange) return null;

		const currentIdx = sorted.indexOf(currentRange);

		const nextTotal = ProgressiveDiscount.getTotalPriceForPhotoCount(rangesPrice, validPhotoCount + 1);
		const nextPhotoPrice = nextTotal - currentTotal;
		const nextAverage = nextTotal / (validPhotoCount + 1);

		for (let i = currentIdx + 1; i < sorted.length; i++) {
			const targetCount = sorted[i].min_photos;
			if (targetCount <= validPhotoCount) continue;
			if (targetCount > maxAvailablePhotos) continue;

			const targetTotal = ProgressiveDiscount.getTotalPriceForPhotoCount(rangesPrice, targetCount);
			const targetAverage = targetTotal / targetCount;

			if (targetAverage < currentAverage) {
				const photosNeeded = targetCount - validPhotoCount;

				if (photosNeeded > 2 && nextPhotoPrice <= currentRange.price) {
					continue;
				}

				const averageSavingsPercent = ProgressiveDiscount.getAverageSavingsPercent(targetAverage, currentAverage);
				const rangeSavingsPercent = ProgressiveDiscount.getRangeSavingsPercent(currentRange.price, sorted[i].price);
				
				return {
					type: 'incentive',
					photosNeeded,
					totalPhotos: targetCount,
					averagePrice: targetAverage,
					averageSavingsPercent,
					rangeSavingsPercent,
					additionalCost: targetTotal - currentTotal,
					currentTotal,
					nextTotal: targetTotal,
				};
			}

			if (sorted[i].price < currentAverage && nextPhotoPrice > currentRange.price) {
				const rangeMaxPhotos = sorted[i].max_photos || targetCount;

				if (rangeMaxPhotos > targetCount) {

					const photosToDropAverage = ProgressiveDiscount.calculatePhotosToDropAverage(targetTotal, targetCount, currentAverage, sorted[i].price);
					
					if (photosToDropAverage <= rangeMaxPhotos && photosToDropAverage > validPhotoCount && photosToDropAverage <= maxAvailablePhotos) {
						const totalPriceWithNeededPhotosToDiscount = ProgressiveDiscount.getTotalPriceForPhotoCount(rangesPrice, photosToDropAverage);
						const averageWithNeededPhotosToDiscount = totalPriceWithNeededPhotosToDiscount / photosToDropAverage;

						if (averageWithNeededPhotosToDiscount < currentAverage) {
							const averageSavingsPercentWithNeededPhotos = ProgressiveDiscount.getAverageSavingsPercent(averageWithNeededPhotosToDiscount, currentAverage);
							const rangeSavingsPercentAtTargetRange = ProgressiveDiscount.getRangeSavingsPercent(currentRange.price, sorted[i].price);
							const additionalCost = totalPriceWithNeededPhotosToDiscount - currentTotal;

							return {
								type: 'incentive',
								photosNeeded: photosToDropAverage - validPhotoCount,
								totalPhotos: photosToDropAverage,
								averagePrice: averageWithNeededPhotosToDiscount,
								averageSavingsPercent: averageSavingsPercentWithNeededPhotos,
								rangeSavingsPercent: rangeSavingsPercentAtTargetRange,
								additionalCost,
								currentTotal,
								nextTotal: totalPriceWithNeededPhotosToDiscount,
							};
						}
					}
				}
			}
		}

		// M4 fallback: no future range boundary lowers the average, but adding +1 photo
		// still drops the average by at least R$0.20. Avoids showing negligible improvements.
		const MIN_AVERAGE_DROP = 0.20;
		if (nextPhotoPrice < currentAverage && (currentAverage - nextAverage) >= MIN_AVERAGE_DROP) {
			const averageSavingsPercent = ProgressiveDiscount.getAverageSavingsPercent(nextAverage, currentAverage);
			return {
				type: 'incentive',
				photosNeeded: 1,
				totalPhotos: validPhotoCount + 1,
				averagePrice: nextAverage,
				averageSavingsPercent: averageSavingsPercent,
				rangeSavingsPercent: 0,
				additionalCost: nextPhotoPrice,
				currentTotal,
				nextTotal,
			};
		}

		// M6: purely increasing ranges (e.g. 10→20→30) — never show a toast,
		// since every next photo would be more expensive (demotivating).
		const isIncreasing = ProgressiveDiscount.hasOnlyIncreasingPrices(sorted);
		if (isIncreasing) return null;

		// M7: next photo is more expensive than the current range and no cheaper
		// range was found ahead (e.g. V-shape ascent) — silence the toast.
		if (nextPhotoPrice > currentRange.price) return null;

		return {
			type: 'lastTier',
			photosNeeded: 1,
			totalPhotos: validPhotoCount + 1,
			averagePrice: nextAverage,
			averageSavingsPercent: 0,
			rangeSavingsPercent: 0,
			additionalCost: nextPhotoPrice,
			currentTotal,
			nextTotal,
		};
	}
}

export default ProgressiveDiscount;
